import {
  IAccount,
  IActivity,
  IClient,
  IMember,
  INewUser
} from "@wingspanhq/users";
import {
  IAccountRequirementsRequest,
  IAccountRequirementsResponse,
  IGetAvailableCurrenciesForCountryRequest,
  IGetAvailableCurrenciesForCountryResponse
} from "@wingspanhq/users/dist/lib/interfaces/api/account-requirements";
import {
  ILinkTokenResponse,
  IOrganizationUser,
  LinkTokenCreateRequest,
  usersService
} from "../../services/users";
import {
  WSInfiniteQueryConfig,
  WSQueryCache,
  WSQueryConfig
} from "@ws-react-query";
import flatten from "lodash/flatten";
import times from "lodash/times";
import { concurrentActions, WSServiceError } from "../../utils/serviceHelper";
import { useWSInfiniteQuery, useWSQuery } from "../helpers";
import {
  QUERY_ACCOUNT_REQUIREMENTS,
  QUERY_ACCOUNT_REQUIREMENTS_AVAILABLE_CURRENCIES_FOR_COUNTRY,
  QUERY_USERS_ACCOUNT,
  QUERY_USERS_ACCOUNT_LINK_TOKEN,
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_ALL_ORGANIZATION_USERS,
  QUERY_USERS_ALL_ORGANIZATION_USERS_LIST_SIZE,
  QUERY_USERS_CLIENT,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_ORGANIZATION_USER,
  QUERY_USERS_ORGANIZATION_USERS,
  QUERY_USERS_USER_PROFILE
} from "./keys";

export const useQueryUserProfile = (
  userId: string,
  queryConfig?: WSQueryConfig<INewUser, WSServiceError>
) => {
  return useWSQuery(
    QUERY_USERS_USER_PROFILE,
    async () => await usersService.user.get(userId),
    {
      enabled: !!userId,
      refetchOnMount: false,
      ...queryConfig
    }
  );
};

export const useQueryActivities = (
  userId: string,
  queryConfig?: WSQueryConfig<IActivity, WSServiceError>
) => {
  return useWSQuery(
    [QUERY_USERS_ACTIVITIES, userId],
    async () => {
      try {
        return await usersService.activity.get(userId);
      } catch {
        const defaultData: IActivity = {
          events: {
            incomeReviewedAt: new Date(0),
            expensesReviewedAt: new Date(0)
          },
          flows: {},
          userId
        };

        return defaultData;
      }
    },
    {
      enabled: !!userId,
      ...queryConfig
    }
  );
};

export const useQueryMemberProfile = (
  memberId: string,
  queryConfig?: WSQueryConfig<IMember, WSServiceError>
) => {
  return useWSQuery(
    QUERY_USERS_MEMBER_PROFILE,
    async () => {
      return await usersService.member.get(memberId, {
        headers: { "x-ws-fe-flow": "query" }
      });
    },

    queryConfig
  );
};

export const useClientQuery = (
  clientId: string,
  queryConfig?: WSQueryConfig<IClient, WSServiceError>
) =>
  useWSQuery(
    QUERY_USERS_CLIENT,
    () =>
      usersService.client.get(clientId, {
        headers: { "x-ws-fe-flow": "query" }
      }),
    {
      retry: false,
      ...queryConfig
    }
  );

export const useQueryAccount = (
  accountId: string,
  queryConfig?: WSQueryConfig<IAccount, WSServiceError>
) => {
  return useWSQuery(
    [QUERY_USERS_ACCOUNT, accountId],
    () => usersService.account.get(accountId),
    queryConfig
  );
};

export const useQueryAccounts = (
  queryConfig?: WSQueryConfig<IAccount[], WSServiceError>
) => {
  return useWSQuery(
    QUERY_USERS_ACCOUNTS,
    usersService.account.list,
    queryConfig
  );
};

export const useQueryUserAccountLinkToken = (
  request: LinkTokenCreateRequest = {},
  queryConfig?: WSQueryConfig<ILinkTokenResponse, WSServiceError>
) =>
  useWSQuery(
    [QUERY_USERS_ACCOUNT_LINK_TOKEN, request],
    () => usersService.accountLink.create(request),
    {
      ...queryConfig,
      retry: 1
    }
  );

export const useQueryOrganizationUser = (
  userId: string,
  queryConfig?: WSQueryConfig<IMember, WSServiceError>
) => {
  return useWSQuery(
    [QUERY_USERS_ORGANIZATION_USER, userId],
    async () => {
      return await usersService.member.get(userId);
    },

    queryConfig
  );
};

export const useQueryOrganizationUsers = (
  params: {
    sort?: any;
    filter?: any;
  },
  queryConfig?: WSInfiniteQueryConfig<IOrganizationUser[], unknown>
) =>
  useWSInfiniteQuery(
    [QUERY_USERS_ORGANIZATION_USERS, params],
    async ({ pageParam = 1 }) => {
      return await usersService.organization.users.list({
        page: {
          size: 10,
          number: pageParam
        },
        sort: params.sort,
        filter: params.filter
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < 10) {
          return undefined;
        }

        return allPages.length + 1;
      },
      ...queryConfig
    }
  );

export const useQueryOrganizationUsersListSize = (
  params?: {
    sort?: any;
    filter?: any;
  },
  queryConfig?: WSQueryConfig<number, unknown>
) =>
  useWSQuery(
    [QUERY_USERS_ALL_ORGANIZATION_USERS_LIST_SIZE, params],
    async () => {
      const listSize = await usersService.organization.users.listSize();

      return listSize;
    },
    {
      refetchOnMount: false,
      ...queryConfig
    }
  );

export const useQueryAllOrganizationUsers = (
  params?: {
    sort?: any;
    filter?: any;
  },
  queryConfig?: WSQueryConfig<INewUser[], WSServiceError>
) =>
  useWSQuery(
    [QUERY_USERS_ALL_ORGANIZATION_USERS, params],
    async () => {
      const listSize = await usersService.organization.users.listSize();

      WSQueryCache.setQueryData(
        [QUERY_USERS_ALL_ORGANIZATION_USERS_LIST_SIZE, undefined],
        listSize
      );

      const pages = Math.ceil(listSize / 100);

      const actions = times(pages).map(
        (_, i) => () =>
          usersService.organization.users.list({
            page: { size: 100, number: i + 1 }
          })
      );

      const allPages = await concurrentActions(actions, {
        concurrentLimit: 5
      });

      return flatten(allPages);
    },
    {
      refetchOnMount: false,
      ...queryConfig
    }
  );

export const useQueryAccountRequirements = (
  data: IAccountRequirementsRequest,
  config?: WSQueryConfig<IAccountRequirementsResponse, WSServiceError>
) =>
  useWSQuery(
    [QUERY_ACCOUNT_REQUIREMENTS, data.country, data.currency],
    () => usersService.accountRequirements.get(data),
    config
  );

export const useQueryAvailableCurrenciesForCountry = (
  data: IGetAvailableCurrenciesForCountryRequest,
  config?: WSQueryConfig<
    IGetAvailableCurrenciesForCountryResponse,
    WSServiceError
  >
) =>
  useWSQuery(
    [QUERY_ACCOUNT_REQUIREMENTS_AVAILABLE_CURRENCIES_FOR_COUNTRY, data.country],
    () =>
      usersService.accountRequirements.getAvailableCurrenciesForCountry(data),
    config
  );
